import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';

import { select, Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  Subscription
} from 'rxjs';

import {
  DireccionGeograficaFormInput,
  FormGroupConfig,
  Localidad
} from '@rpe/registro-personas-empresas-lib';

import {
  createEmptyPosition,
  Position
} from 'projects/registro-personas-empresas/src/app/gis/gis.model';
import { State } from '../../../vista-usuario/vista-usuario.state';
import { entityDireccionGeograficaExactaIDELoadRequest } from '../../entity-direcciones-geografica-ide-exacta/entity-direcciones-geografica-ide.actions';
import { entityDireccionesGeograficaIDELoadRequest } from '../../entity-direcciones-geografica-ide/entity-direcciones-geografica-ide.actions';
import { selectAllLocalidades } from '../../entity-localidades/entity-localidades.selectors';
import { DireccionGeograficaSet } from '../direcciones-geograficas.model';
import { selectDireccionGeograficaInitialDataByMap } from '../direcciones-geograficas.selectors';

import { v4 as uuidv4 } from 'uuid';
import { entityLocalidadesIDELoadRequest } from '../../entity-localidades-ide/entity-localidades-ide.actions';
import { LocalidadIDE } from '../../entity-localidades-ide/entity-localidades-ide.model';
import {
  selectAllLocalidadesIDE,
  selectLocalidadesIDEByDepartamento
} from '../../entity-localidades-ide/entity-localidades-ide.selectors';
import { DireccionGeografica } from '../direcciones-geografica.model';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'rpe-direcciones-geograficas-form',
  templateUrl: './direcciones-geograficas-form.component.html',
  styleUrls: ['./direcciones-geograficas-form.component.scss']
})
export class DireccionesGeograficasFormComponent implements OnInit, OnDestroy {
  @Input()
  set formInput(dg: DireccionGeografica) {
    if (dg != null) {
      this._direccionGeografica = {
        ...dg,
        pais_codigo: dg.pais_codigo || 'uy'
      };
      this.setValues(this._direccionGeografica);
    }
  }

  get formInput(): DireccionGeografica {
    return this._direccionGeografica;
  }

  @Input() withCountryOption: boolean = true;

  @Output() formStatusChanges = new EventEmitter<string>();
  @Output() formValueChanges = new EventEmitter<DireccionGeografica>();

  _formInput: DireccionGeograficaFormInput = null;
  _direccionGeografica: DireccionGeografica = {
    id: null,
    pais_codigo: 'uy',
    departamento_codigo: null,
    localidad_id: null,
    localidad_nombre: null,
    codigo_postal: null,
    direccion: null,
    longitud: null,
    latitud: null
  };

  esUruguay: Boolean = true;
  direccionGeograficaFormGroup: FormGroup = this.createGroup();
  primeraVez: Boolean = true;
  cargaInicial = true;
  direccionGeograficaSet$: Observable<DireccionGeograficaSet>;
  direccionGeograficaSet: DireccionGeograficaSet;

  localidades$: Observable<Localidad[]>;
  selectedDepartamento$ = new BehaviorSubject<string>(null);
  departamentoNombre: string = null;
  localidadesFiltrada$: Observable<LocalidadIDE[]>;
  position: Position = createEmptyPosition();
  ultimoDepartamentoCargado: string = null; // agregar esta variable para rastrear el último departamento
  ultimaDireccionBuscada: string = null; // evitar búsquedas repetidas de la misma dirección

  subscription: Subscription;
  subscriptionCombine: Subscription;

  idMap: string = uuidv4();

  constructor(private fb: FormBuilder, private store: Store<State>) { }

  ngOnInit(): void {
    // Load inital data
    this.direccionGeograficaSet$ = this.store.pipe(
      select(selectDireccionGeograficaInitialDataByMap(this.idMap))
    );
    this.subscription = this.direccionGeograficaSet$.subscribe((dgs) => {
      this.direccionGeograficaSet = dgs;
      
      // Buscar el nombre de la localidad si falta
      if (dgs?.localidadesIDE && this.direccionGeograficaFormGroup.get('localidad_id')?.value) {
        const localidadId = this.direccionGeograficaFormGroup.get('localidad_id').value;
        if (!this.direccionGeograficaFormGroup.get('localidad_nombre').value) {
          const localidad = dgs.localidadesIDE.find((l) => l.id === localidadId);
          if (localidad) {
            this.direccionGeograficaFormGroup.patchValue({
              localidad_nombre: localidad.nombre
            }, { emitEvent: false });
          }
        }
      }
      
      if (
        this.direccionGeograficaSet.direccionGeograficaIDEExacta?.lat &&
        this.position.latitud !=
        this.direccionGeograficaSet.direccionGeograficaIDEExacta.lat
      ) {
        this.setPosition(
          this.direccionGeograficaSet.direccionGeograficaIDEExacta.lat,
          this.direccionGeograficaSet.direccionGeograficaIDEExacta.lng
        );
      }
    });

    // Seteo las localidades del departamento
    this.subscriptionCombine = combineLatest([
      this.direccionGeograficaSet$,
      this.selectedDepartamento$.pipe(startWith(null))
    ]).subscribe(([dgs, d]) => {
      if (dgs && d && d !== this.ultimoDepartamentoCargado) {
        // solo ejecutar si el departamento realmente cambió
        this.ultimoDepartamentoCargado = d;
        const dMat: MatSelectChange = {
          value: d,
          source: null
        };
        this.onDepartamentoSelect(dMat);
      }
    });

    // Filtro de las localidades por departamento
    this.localidades$ = this.store.pipe(select(selectAllLocalidades));

    this.direccionGeograficaFormGroup.valueChanges.subscribe((_) => {
      this.formValueChanges.emit({
        ...this.direccionGeograficaFormGroup.value
      });
    });
    this.direccionGeograficaFormGroup.statusChanges.subscribe((f) => {
      return this.formStatusChanges.emit(f);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.subscriptionCombine?.unsubscribe();
  }

  setPosition(lat: number, long: number) {
    this.position = {
      latitud: lat,
      longitud: long
    };
  }

  setValues(direccionGeo: DireccionGeografica): void {
    this.primeraVez = false;
    if (direccionGeo && direccionGeo !== null) {
      // Para cargar combo de localidades con las del departamento
      this.direccionGeograficaFormGroup.patchValue(
        {
          pais_codigo: direccionGeo.pais_codigo,
          departamento_codigo: direccionGeo.departamento_codigo,
          localidad_id: direccionGeo.localidad_id,
          localidad_nombre: direccionGeo.localidad_nombre,
          codigo_postal: direccionGeo.codigo_postal,
          direccion: direccionGeo.direccion,
          longitud: direccionGeo.longitud,
          latitud: direccionGeo.latitud
        },
        { emitEvent: false }
      );
      this.selectedDepartamento$.next(direccionGeo.departamento_codigo);
      this.esUruguay = direccionGeo.pais_codigo == 'uy';
      // Position gis
      this.position = {
        latitud: direccionGeo.latitud,
        longitud: direccionGeo.longitud
      };
      
      // Disparar búsqueda de direcciones IDE solo si viene de DGI (sin coordenadas)
      // y si la dirección cambió respecto a la última búsqueda
      if (direccionGeo.direccion &&
          direccionGeo.direccion.length >= 4 &&
          this.esUruguay) {
        const address = this.createAddress(direccionGeo.direccion);
        if (address && address.length >= 4 && address !== this.ultimaDireccionBuscada) {
          this.ultimaDireccionBuscada = address;
          setTimeout(() => {
            this.store.dispatch(
              entityDireccionesGeograficaIDELoadRequest({
                q: address,
                mapa: this.idMap
              })
            );
          }, 1500);
        }
      }
    }
    // Luego de crear emito status
    this.formStatusChanges.emit(this.direccionGeograficaFormGroup.status);
  }

  // subform - https://tomastrajan.medium.com/angular-reactive-sub-forms-type-safe-without-duplication-dbd24225e1e8
  createGroup(): FormGroup {
    const config: FormGroupConfig<DireccionGeografica> = {
      id: [, []],
      pais_codigo: ['uy', [Validators.required]], // codigo Pais
      departamento_codigo: [, [Validators.required]], // codigo Departamento
      localidad_id: [, [Validators.required]], // id Localidad
      localidad_nombre: [, []],
      codigo_postal: [, []],
      direccion: ['', [Validators.required]],
      longitud: [, [Validators.required]],
      latitud: [, [Validators.required]]
    };
    this.direccionGeograficaFormGroup = this.fb.group(config);
    return this.direccionGeograficaFormGroup;
  }

  limpiarCampos() {
    this.direccionGeograficaFormGroup.get('pais_codigo').setValue(null);
    this.direccionGeograficaFormGroup.get('departamento_codigo').setValue(null);
    this.direccionGeograficaFormGroup.get('localidad_id').setValue(null);
    this.direccionGeograficaFormGroup.get('codigo_postal').setValue(null);
    this.direccionGeograficaFormGroup.get('direccion').setValue(null);
    this.direccionGeograficaFormGroup.get('longitud').setValue(null);
    this.direccionGeograficaFormGroup.get('latitud').setValue(null);
  }

  onPaisSelect(event: MatSelectChange): void {
    this.esUruguay = event.value == 'uy';
    this.direccionGeograficaFormGroup.get('departamento_codigo').setValue(null);
    this.direccionGeograficaFormGroup.get('localidad_id').setValue(null);
    if (event.value == 'uy') {
      this.direccionGeograficaFormGroup
        .get('departamento_codigo')
        .setValidators(Validators.required);
      this.direccionGeograficaFormGroup
        .get('localidad_id')
        .setValidators(Validators.required);
    } else {
      this.direccionGeograficaFormGroup
        .get('departamento_codigo')
        .clearValidators();
      this.direccionGeograficaFormGroup.get('localidad_id').clearValidators();
    }
    this.direccionGeograficaFormGroup
      .get('departamento_codigo')
      .updateValueAndValidity();
    this.direccionGeograficaFormGroup
      .get('localidad_id')
      .updateValueAndValidity();
  }

  onDepartamentoSelect(event: MatSelectChange): void {
    // Se filtra el set de localidades por el departamento
    const departamento = this.direccionGeograficaSet.departamentos.find(
      (d) => d.codigo == event.value
    );
    this.departamentoNombre = departamento.nombre;
    this.localidadesFiltrada$ = this.store.pipe(
      select(selectLocalidadesIDEByDepartamento(this.departamentoNombre))
    );
    if (departamento) {
      this.store.dispatch(
        entityLocalidadesIDELoadRequest({
          q: departamento.nombre
        })
      );
    }
  }

  onLocalidadSelect(event: MatSelectChange): void {
    const localidad = this.direccionGeograficaSet.localidadesIDE.find(
      (l) => l.id == event.value
    );
    this.direccionGeograficaFormGroup.patchValue({
      localidad_nombre: localidad.nombre
    });
  }

  onDireccionKeyUp(event: any): void {
    if (this.esUruguay) {
      // Filtro las flechitas y el enter para que no haga nada
      if (
        !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(
          event.key
        )
      ) {
        const address = this.createAddress(event.target.value);
        if (event.target.value?.length >= 4) {
          this.store.dispatch(
            entityDireccionesGeograficaIDELoadRequest({
              q: address,
              mapa: this.idMap
            })
          );
        }
      }
    }
  }

  onDireccionSelect(address: string) {
    const direccionIDE =
      this.direccionGeograficaSet.direccionesGeograficasIDE.find(
        (dide) => dide.address == address
      );
    if (direccionIDE) {
      if (direccionIDE.lat == 0 || direccionIDE.lng == 0) {
        this.store.dispatch(
          entityDireccionGeograficaExactaIDELoadRequest({
            d: direccionIDE,
            mapa: this.idMap
          })
        );
      } else {
        this.setPosition(direccionIDE.lat, direccionIDE.lng);
      }
    }
  }

  createAddress(address: string) {
    let direccion = address;
    if (!this.direccionGeograficaSet?.departamentos) {
      return direccion;
    }
    const d = this.direccionGeograficaSet.departamentos.find(
      (d) =>
        d.codigo ==
        this.direccionGeograficaFormGroup.get('departamento_codigo').value
    );
    const l = this.direccionGeograficaSet.localidadesIDE?.find(
      (l) => l.id == this.direccionGeograficaFormGroup.get('localidad_id').value
    );
    if (l) {
      direccion += ', ' + l.nombre;
    }
    if (d) {
      direccion += ', ' + d.nombre;
    }
    return direccion;
  }

  onChangePosition(p: Position) {
    this.direccionGeograficaFormGroup.patchValue(
      {
        longitud: p.longitud,
        latitud: p.latitud
      },
      { emitEvent: true }
    );
    this.direccionGeograficaFormGroup.get('longitud').updateValueAndValidity();
    this.direccionGeograficaFormGroup.get('latitud').updateValueAndValidity();
  }

  checkErrors() {
    this.direccionGeograficaFormGroup.markAllAsTouched();
  }
}
